import { useEffect, useState, type FormEvent } from 'react'
import { KeyRound, Link2, Plus, Trash2 } from 'lucide-react'
import { api } from '../../api/client'
import type { AppPassword, User } from '../../api/types'
import { PageHeader } from '../../components/PageHeader'
import { Modal } from '../../components/Modal'
import { useI18n } from '../../i18n'

export function UserSettingsPage() {
  const { t, locale, setLocale } = useI18n()
  const [profile, setProfile] = useState<User | null>(null)
  const [passwords, setPasswords] = useState<AppPassword[]>([])
  const [status, setStatus] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [appModal, setAppModal] = useState(false)
  const [appName, setAppName] = useState('')

  useEffect(() => {
    api.get<User>('/profile').then(setProfile).catch((reason: Error) => setStatus(reason.message))
    api.get<AppPassword[]>('/app-passwords').then(setPasswords).catch((reason: Error) => setStatus(reason.message))
  }, [])

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      await api.patch('/profile', { displayName: form.get('displayName'), replyTo: form.get('replyTo'), forwardTo: form.get('forwardTo'), signature: form.get('signature'), language: form.get('language'), autoReply: form.get('autoReply') === 'on' })
      setLocale(String(form.get('language')) as typeof locale)
      setStatus('Saved.')
    } catch (reason) { setStatus(reason instanceof Error ? reason.message : 'Unable to save settings.') }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formEl = event.currentTarget
    const form = new FormData(formEl)
    const password = String(form.get('password'))
    const confirmation = String(form.get('confirmation'))
    if (password !== confirmation) { setStatus('Passwords do not match.'); return }
    try { await api.post('/profile/password', { password }); setStatus('Password changed.'); formEl.reset() }
    catch (reason) { setStatus(reason instanceof Error ? reason.message : 'Unable to change password.') }
  }

  async function createAppPassword() {
    if (!appName.trim()) return
    try {
      const result = await api.post<{ appPassword: AppPassword; password: string }>('/app-passwords', { name: appName.trim() })
      setPasswords((current) => [result.appPassword, ...current])
      setNewPassword(result.password)
      setAppModal(false)
      setAppName('')
    } catch (reason) { setStatus(reason instanceof Error ? reason.message : 'Unable to create app password.') }
  }

  async function removeAppPassword(id: string) {
    try { await api.delete(`/app-passwords/${id}`); setPasswords((current) => current.filter((password) => password.id !== id)) }
    catch (reason) { setStatus(reason instanceof Error ? reason.message : 'Unable to revoke app password.') }
  }

  return <>
    <PageHeader title="user.settings" description="user.settingsDescription" />
    <form key={profile?.id ?? 'loading'} onSubmit={save} className="panel max-w-2xl p-5 sm:p-6">
      <h2 className="font-semibold">{t('user.profile')}</h2>
      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        <label className="text-sm font-medium">{t('common.email')}<input className="field mt-1.5 bg-muted/50" value={profile?.email ?? ''} readOnly /></label>
        <label className="text-sm font-medium">{t('auth.username')}<input className="field mt-1.5 bg-muted/50" value={profile?.username ?? ''} readOnly /></label>
        <label className="text-sm font-medium">{t('user.displayName')}<input className="field mt-1.5" name="displayName" defaultValue={profile?.displayName} /></label>
        <label className="text-sm font-medium">{t('user.replyTo')}<input className="field mt-1.5" name="replyTo" type="email" defaultValue={profile?.replyTo} /></label>
        <label className="text-sm font-medium">{t('user.forwardTo')}<input className="field mt-1.5" name="forwardTo" type="email" defaultValue={profile?.forwardTo} /></label>
      </div>
      <label className="mt-5 block text-sm font-medium">{t('common.language')}<select className="field mt-1.5" name="language" defaultValue={profile?.language ?? locale}><option value="en">English</option><option value="zh">中文</option></select></label>
      <label className="mt-5 block text-sm font-medium">{t('user.signature')}<textarea className="field mt-1.5 h-24 py-2" name="signature" defaultValue={profile?.signature} /></label>
      <div className="mt-6 border-t pt-5">
        <h2 className="font-semibold">{t('user.autoReply')}</h2>
        <label className="mt-3 flex items-center gap-2 text-sm"><input name="autoReply" type="checkbox" defaultChecked={profile?.autoReply} />{t('user.enableAutoReply')}</label>
      </div>
      <button className="btn-primary mt-6">{t('common.save')}</button>
    </form>

    <form onSubmit={changePassword} className="panel mt-5 max-w-2xl p-5 sm:p-6">
      <h2 className="font-semibold">{t('user.changePassword')}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <input className="field" name="password" type="password" minLength={8} placeholder={t('user.newPassword')} required />
        <input className="field" name="confirmation" type="password" minLength={8} placeholder={t('user.confirmPassword')} required />
      </div>
      <button className="btn-secondary mt-4">{t('user.changePassword')}</button>
    </form>

    <section className="panel mt-5 max-w-2xl p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">{t('user.appPasswords')}</h2>
          <p className="text-sm text-muted-foreground">{t('user.appPasswordsDescription')}</p>
        </div>
        <button type="button" onClick={() => setAppModal(true)} className="btn-primary flex items-center gap-2 shrink-0"><Plus size={16}/>{t('user.createAppPassword')}</button>
      </div>

      <div className="mt-4 rounded-lg border bg-muted/30 p-4">
        <h3 className="text-sm font-medium mb-3">{t('user.smtpInfo')}</h3>
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <div className="flex justify-between sm:block"><span className="text-muted-foreground">{t('user.smtpServer')}</span><span className="font-mono ml-2 sm:ml-0 sm:block">smtp.1444144.xyz</span></div>
          <div className="flex justify-between sm:block"><span className="text-muted-foreground">{t('user.smtpPort')}</span><span className="font-mono ml-2 sm:ml-0 sm:block">587</span></div>
          <div className="flex justify-between sm:block"><span className="text-muted-foreground">{t('user.smtpEncryption')}</span><span className="font-mono ml-2 sm:ml-0 sm:block">STARTTLS</span></div>
          <div className="flex justify-between sm:block"><span className="text-muted-foreground">{t('user.smtpUsername')}</span><span className="font-mono ml-2 sm:ml-0 sm:block">{profile?.email ?? '—'}</span></div>
          <div className="sm:col-span-2 flex justify-between sm:block"><span className="text-muted-foreground">{t('user.smtpPassword')}</span><span className="ml-2 sm:ml-0 sm:block">{t('user.smtpUseAppPassword')}</span></div>
        </div>
      </div>

      {newPassword && <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-950"><KeyRound className="mr-2 inline" size={16}/><code className="break-all">{newPassword}</code></div>}
      <div className="mt-4 divide-y">
        {passwords.map((password) => (
          <div className="flex items-center justify-between py-3 text-sm" key={password.id}>
            <span className="truncate mr-2">{password.name} · <code className="text-muted-foreground">{password.prefix}…</code></span>
            <button onClick={() => removeAppPassword(password.id)} className="shrink-0 rounded-lg p-2 text-destructive transition-colors hover:bg-destructive/10" aria-label="Revoke application password"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
      {status && <p className="mt-4 text-sm text-muted-foreground">{status}</p>}
    </section>

    {/* SSO binding */}
    <section className="panel mt-5 max-w-2xl p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">{t('user.ssoAccount')}</h2>
          <p className="text-sm text-muted-foreground">{t('user.ssoLinkDesc')}</p>
        </div>
        {profile?.oidcIssuer ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700">
            <Link2 size={12} />{t('user.ssoLinked')}
          </span>
        ) : (
          <button type="button" onClick={() => { window.location.href = '/api/v1/auth/sso/authorize' }} className="btn-primary flex items-center gap-2 shrink-0">
            <Link2 size={16} />{t('user.linkSSO')}
          </button>
        )}
      </div>
    </section>

    <Modal open={appModal} onClose={() => { setAppModal(false); setAppName('') }} title={t('user.createAppPassword')}>
      <div className="space-y-4">
        <input className="field" placeholder={t('user.appPasswords')} value={appName} onChange={(e) => setAppName(e.target.value)} autoFocus />
        <div className="flex gap-3">
          <button onClick={createAppPassword} disabled={!appName.trim()} className="btn-primary disabled:opacity-50">{t('user.createAppPassword')}</button>
          <button onClick={() => { setAppModal(false); setAppName('') }} className="btn-secondary">{t('common.cancel')}</button>
        </div>
      </div>
    </Modal>
  </>
}
